import BaseModal from 'components/BaseModal'
import { isValidEntityId } from 'entities-next/common/common.validations'
import { getApiPairingsHandlers } from 'entities-next/deals/pairing.api'
import { FormikHelpers } from 'formik'
import React, { useState } from 'react'
import { NumberFormatValues } from 'react-number-format'
import { NumberField } from 'scenes/Dashboard/components-next/Forms/Controllers'
import SubmitButton from 'scenes/Dashboard/components/SubmitButton'
import { OrganizationNotFoundError } from 'scenes/Dashboard/data/organizations/errors'
import { CreateBasicPairing } from 'scenes/Dashboard/data/pairings/types'
import { ScoutingCampaignNotFoundError } from 'scenes/Dashboard/data/scouting-campaigns/errors'
import { UnvalidatedFormikData } from 'services/api'
import { FastField, FormManager } from 'utils-next/forms'
import { assertIsError } from 'utils/errors'
import * as Yup from 'yup'

import { Button, Theme } from '@mui/material'
import { createStyles, withStyles, WithStyles } from '@mui/styles'

export type NewPairingModalProps = {
  onClose: () => unknown
} & WithStyles<typeof styles>

const NewPairingModal: React.FunctionComponent<NewPairingModalProps> = (props) => {
  const { onClose, classes } = props

  return (
    <BaseModal className={classes.modal} onClose={onClose} open={true} title={'Create a new pairing'}>
      <NewPairingForm {...props} />
    </BaseModal>
  )
}

type PairingCreationState = {
  errorMessage?: string
  confirmationMessage?: string
}

type NewPairingFormProps = {
  onClose: () => void
  validationError?: string
} & WithStyles<typeof styles>

const NewPairingForm = (props: NewPairingFormProps) => {
  const { onClose, classes } = props
  const { apiCreateNewPairing } = getApiPairingsHandlers()

  const [pairingCreationState, setPairingCreationState] = useState<PairingCreationState>({})

  const { confirmationMessage, errorMessage } = pairingCreationState

  async function handleSubmit(data: UnvalidatedFormikData, formikHelpers: FormikHelpers<UnvalidatedFormikData>) {
    try {
      const basicPairing = parseFormikData(data)
      const pairing = await apiCreateNewPairing(basicPairing)

      setPairingCreationState({
        confirmationMessage: `The pairing ${pairing.id} has been created`,
      })
    } catch (error) {
      assertIsError(error)

      const errorState: PairingCreationState = {}

      if (error instanceof OrganizationNotFoundError) {
        formikHelpers.setFieldError('organizationId', error.message)
      } else if (error instanceof ScoutingCampaignNotFoundError) {
        formikHelpers.setFieldError('scoutingCampaignId', error.message)
      } else {
        errorState.errorMessage = error.message
      }

      setPairingCreationState(errorState)
    }
  }

  return (
    <FormManager initialValues={{}} onSubmit={handleSubmit} validationSchema={NewPairingFormSchema}>
      {(formProps) => (
        <>
          {confirmationMessage && (
            <p>
              <text className={classes.confirmationMessage}>{confirmationMessage}</text>
            </p>
          )}
          {errorMessage && (
            <p>
              <text className={classes.errorMessage}>{errorMessage}</text>
            </p>
          )}

          <FastField
            className={classes.formField}
            component={NumberField}
            adornmentText={'Space'}
            placeholder="Id"
            name="scoutingCampaignId"
            internalInputProps={{
              thousandSeparator: '',
              isAllowed: ({ floatValue }: NumberFormatValues) =>
                floatValue === undefined || isValidEntityId(floatValue),
            }}
            InputLabelProps={{
              shrink: true,
            }}
            oncha
          />

          <FastField
            className={classes.formField}
            component={NumberField}
            adornmentText={'Organization'}
            placeholder="Id"
            name="organizationId"
            internalInputProps={{
              thousandSeparator: '',
              isAllowed: ({ floatValue }: NumberFormatValues) =>
                floatValue === undefined || isValidEntityId(floatValue),
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <div className={classes.formFooter}>
            <Button variant="text" size="medium" onClick={onClose}>
              Discard
            </Button>
            <SubmitButton label="Save" onClick={formProps.submitForm} />
          </div>
        </>
      )}
    </FormManager>
  )
}

const NewPairingFormSchema = Yup.object().shape({
  scoutingCampaignId: Yup.number().required(),
  organizationId: Yup.number().required(),
})

function parseFormikData(data: UnvalidatedFormikData): CreateBasicPairing {
  const { organizationId, scoutingCampaignId } = {
    organizationId: Number(data.organizationId),
    scoutingCampaignId: Number(data.scoutingCampaignId),
  }

  if (!isValidEntityId(organizationId) || !isValidEntityId(scoutingCampaignId)) {
    throw new Error('The organization and the Space must be valid entity ids')
  }

  return {
    organizationId,
    scoutingCampaignId,
  }
}

const styles = ({ spacing }: Theme) =>
  createStyles({
    formFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    modal: {
      width: 700,
    },
    formField: {
      width: '70%',
      marginTop: spacing(1),
      marginBottom: spacing(3),
      WebkitAppearance: 'none',
    },
    errorMessage: {
      color: 'red',
    },
    confirmationMessage: {
      color: 'green',
    },
  })

NewPairingModal.displayName = 'NewPairingModal'

export default withStyles(styles)(NewPairingModal)
